import {useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent} from "react";
import {io, Socket} from "socket.io-client";
import {DATE_LOCALE, makeT, resolveLang, type Lang, type StringKey, type T} from "./i18n";
import {CloseIcon, EndChatIcon, LauncherIcon, MailIcon, PaperclipIcon} from "./icons";
import {
    GREETING_DISMISSED_KEY,
    GREETING_SESSION_KEY,
    GREETING_SNOOZE_MS,
    type PreChatForm,
    type Proactive,
} from "./engagement";

type BootstrapRes = {
    token: string;
    conversationId: string;
    settings: Record<string, any>;
    anyAgentOnline?: boolean;
    agentStatus?: AgentStatus;
    contactKnown?: { name: boolean; email: boolean };
    conversationStarted?: boolean;
};

type AgentStatus = "online" | "away" | "offline";

function toAgentStatus(status: unknown, anyAgentOnline: unknown): AgentStatus {
    if (status === "online" || status === "away" || status === "offline") return status;
    return anyAgentOnline ? "online" : "offline";
}

type Props = {
    apiBase: string,
    realtimeUrl: string,
    token: string,
    conversationId: string,
    anyAgentOnline?: boolean,
    // online | away (nobody Online, someone briefly away - chat, answered soon) | offline
    agentStatus?: AgentStatus,
    title?: string,
    position?: "left" | "right",
    offsetX?: number,
    offsetY?: number,
    language?: string,
    primaryColor?: string,
    launcherEmoji?: string,
    preChatForm?: PreChatForm,
    proactive?: Proactive,
    // whether the visitor already left a name / email (so the pre-chat form skips them)
    contactKnown?: { name: boolean; email: boolean },
    // the bootstrapped conversation already has messages - no proactive greeting then
    conversationStarted?: boolean,
    preview?: boolean,
    onRebootstrap?: () => Promise<BootstrapRes>
};

type Msg = {
    messageId: string;
    clientMsgId?: string | null;
    senderType: "visitor" | "agent" | "bot" | "system";
    body: string;
    createdAt: string;
    status?: "sending" | "sent" | "failed" | "seen";
    deliveredAt?: string | null;
    seenAt?: string | null;
    senderName?: string | null;
    senderUserId?: string | null;
    metadata?: Record<string, any> | null;
    attachments?: Array<{
        id?: string;
        name: string;
        url: string;
        mime?: string | null;
        size?: number | null;
    }>;
};

function formatTime(iso: string, lang: Lang) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(DATE_LOCALE[lang], {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}




// Launcher position the visitor dragged to: distance from the chosen side edge (x)
// and from the bottom (y). Side follows whichever half of the screen it was dropped in,
// so the chat window always opens toward the center.
type LauncherPos = { side: "left" | "right"; x: number; y: number };

const LAUNCHER_POS_KEY = "cw_launcher_pos";
const LAUNCHER_SIZE = 54;
const EDGE_GAP = 12;
const DRAG_THRESHOLD = 5;
const DOUBLE_CLICK_MS = 250;

function loadLauncherPos(): LauncherPos | null {
    try {
        const raw = localStorage.getItem(LAUNCHER_POS_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw);
        if ((p?.side === "left" || p?.side === "right") && Number.isFinite(p?.x) && Number.isFinite(p?.y)) {
            return p as LauncherPos;
        }
    } catch {
        /* ignore */
    }
    return null;
}

function saveLauncherPos(p: LauncherPos) {
    try {
        localStorage.setItem(LAUNCHER_POS_KEY, JSON.stringify(p));
    } catch {
        /* ignore */
    }
}

function clearLauncherPos() {
    try {
        localStorage.removeItem(LAUNCHER_POS_KEY);
    } catch {
        /* ignore */
    }
}

// "What's new" card shown once over the chat window. Bump `id` when adding a new
// release so returning visitors see it again.
const WHATS_NEW: { id: string; items: Array<{ icon: string; title: StringKey; text: StringKey }> } = {
    id: "2026-09-26-movable-launcher",
    items: [
        { icon: "✋", title: "whatsNew.moveTitle", text: "whatsNew.moveText" },
        { icon: "🟢", title: "whatsNew.onlineTitle", text: "whatsNew.onlineText" },
    ],
};
const WHATS_NEW_KEY = "cw_whats_new_seen";

function isWhatsNewSeen() {
    try {
        return localStorage.getItem(WHATS_NEW_KEY) === WHATS_NEW.id;
    } catch {
        return true; // no storage -> don't show it on every open
    }
}

// System messages come from the server in English; render them in the widget's language
// from their metadata, falling back to the stored text for anything unknown.
function systemText(m: Msg, t: T): string {
    const md = m.metadata ?? {};
    if (md.escalateNotice === true) {
        const pos = Number(md.queuePosition);
        const min = Number(md.estimatedWaitMinutes);
        return pos > 0 && min > 0 ? t("system.escalateQueue", { pos, min }) : t("system.escalate");
    }
    if (md.closeNotice === true) {
        if (md.closedBy === "agent") return t("system.closedByAgent");
        if (md.closedBy === "visitor") return t("system.closedByVisitor");
    }
    if (md.autoClose === true) return t("system.autoClose");
    if (md.ticketNotice === true && md.ticketNumber) {
        return t("system.ticket", { n: md.ticketNumber, email: String(md.email ?? "") });
    }
    return m.body;
}

function clamp(v: number, min: number, max: number) {
    return Math.min(Math.max(v, min), Math.max(min, max));
}

const RECONNECT_NOTICE_DELAY_MS = 4000;

export function WidgetApp(props: Props) {
    const lang = useMemo(() => resolveLang(props.language), [props.language]);
    const t = useMemo(() => makeT(lang), [lang]);

    const [open, setOpen] = useState(false);
    // an agent wrote while the window was closed (e.g. "Start chat" from the console's
    // visitor list): show it next to the launcher so it isn't missed
    const [incoming, setIncoming] = useState<{ from: string; body: string; count: number } | null>(null);
    useEffect(() => {
        if (open) setIncoming(null);
    }, [open]);

    // "Support" is the title every widget is created with; show it in the widget's language
    // unless the admin actually changed it.
    const headerTitle = !props.title || props.title.trim() === "Support" ? t("header.defaultTitle") : props.title;

    // "Reconnecting…" only after a real connection dropped for a while — not before the
    // first connect and not for a blip.
    const [reconnecting, setReconnecting] = useState(false);
    const everConnectedRef = useRef(false);
    const reconnectNoticeTimer = useRef<number | null>(null);

    const [launcherPos, setLauncherPos] = useState<LauncherPos | null>(() => loadLauncherPos());
    const [dragging, setDragging] = useState(false);
    const dragRef = useRef<{ pointerId: number; startX: number; startY: number; left: number; top: number; moved: boolean } | null>(null);
    const suppressClickRef = useRef(false);
    const clickTimerRef = useRef<number | null>(null);
    const [showWhatsNew, setShowWhatsNew] = useState(() => !isWhatsNewSeen());

    function dismissWhatsNew() {
        setShowWhatsNew(false);
        try {
            localStorage.setItem(WHATS_NEW_KEY, WHATS_NEW.id);
        } catch {
            /* ignore */
        }
    }
    const [, setViewportTick] = useState(0);

    // re-clamp the launcher/window position when the viewport changes
    useEffect(() => {
        const onResize = () => setViewportTick((t) => t + 1);
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            if (clickTimerRef.current) window.clearTimeout(clickTimerRef.current);
        };
    }, []);
    const [text, setText] = useState("");
    const [messages, setMessages] = useState<Msg[]>([]);
    const [connected, setConnected] = useState(false);

    const [confirmClose, setConfirmClose] = useState(false);

    const [agentTyping, setAgentTyping] = useState(false);
    const [typingActor, setTypingActor] = useState<"agent" | "bot">("agent");

    const typingTimer = useRef<number | null>(null);

    const bodyRef = useRef<HTMLDivElement | null>(null);
    const endRef = useRef<HTMLDivElement | null>(null);

    const isMine = (m: Msg) => m.senderType === "visitor";

    const [showContactForm, setShowContactForm] = useState(false);
    const [contactSubmitted, setContactSubmitted] = useState(false);
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [submittingContact, setSubmittingContact] = useState(false);

    const [closed, setClosed] = useState(false);
    const [rated, setRated] = useState(false);
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingHover, setRatingHover] = useState(0);
    const [ratingComment, setRatingComment] = useState("");
    const [submittingRating, setSubmittingRating] = useState(false);

    const [agentStatus, setAgentStatus] = useState<AgentStatus>(() =>
        toAgentStatus(props.agentStatus, props.anyAgentOnline)
    );
    // "can chat": online or away - only offline switches to the offline form
    const agentOnline = agentStatus !== "offline";
    const agentAway = agentStatus === "away";

// transcript mini-sheet
    const [showTranscript, setShowTranscript] = useState(false);
    const [email, setEmail] = useState("");
    const [sendingTranscript, setSendingTranscript] = useState(false);

// toast-like (mini notice)
    const [notice, setNotice] = useState<{ type: "success" | "error" | "info"; text: string } | null>(null);
    const noticeTimer = useRef<number | null>(null);

    const hasConversationStarted = messages.some(
        (m) => m.senderType === "visitor" || m.senderType === "agent" || m.senderType === "bot"
    );

    // nobody online and the visitor just wrote: ask where to reply (the agent's answer is
    // emailed to them if they've left - see worker run_reply_mail_scheduler)
    const lastChatMsg = [...messages].reverse().find((m) => m.senderType !== "system");
    // the latest message left while nobody was online, as long as no agent has answered since
    const pendingOfflineId = (() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m.senderType === "agent") return null;
            if (m.senderType === "visitor" && m.metadata?.offline) return m.messageId;
        }
        return null;
    })();
    const offlineReplyEmail =
        String(messages.find((m) => m.messageId === pendingOfflineId)?.metadata?.contactEmail ?? "") ||
        (contactSubmitted ? contactEmail.trim() : "");
    const askContactOffline = !agentOnline && !!lastChatMsg && lastChatMsg.senderType === "visitor";

    const showOfflineForm =
        !agentOnline &&
        !closed &&
        !hasConversationStarted;

    // ---- pre-chat form: name / email before the first message (off unless configured).
    // Offline, the offline form already asks for the email, so it wins.
    const [contactKnown, setContactKnown] = useState(props.contactKnown ?? { name: false, email: false });
    const preChat = props.preChatForm;
    const preChatAskName = !!preChat?.enabled && preChat.name !== "off" && !contactKnown.name;
    const preChatAskEmail = !!preChat?.enabled && preChat.email !== "off" && !contactKnown.email;
    const showPreChat =
        (preChatAskName || preChatAskEmail) &&
        !hasConversationStarted &&
        !contactSubmitted &&
        !closed &&
        !showOfflineForm;
    const [preChatTried, setPreChatTried] = useState(false);

    // away: the visitor wrote and waits for an agent to come back - offer (optionally) to
    // leave an email in case they can't wait; the reply is emailed if they've left
    const askContactAway =
        agentAway && !!lastChatMsg && lastChatMsg.senderType === "visitor" && !contactKnown.email;

    // ---- proactive greeting: a bubble next to the launcher after N seconds (off unless
    // configured). At most once per visit, a week's snooze after the visitor closes it,
    // never on top of an ongoing chat.
    const pro = props.proactive;
    const greetingText = ((lang === "sr" ? pro?.messageSr : pro?.messageEn) || "").trim();
    const [greetingDue, setGreetingDue] = useState(false);
    const [greetingVisible, setGreetingVisible] = useState(false);
    const [greetingInChat, setGreetingInChat] = useState(false);
    const greetingDoneRef = useRef(false);

    const [offlineName, setOfflineName] = useState("");
    const [offlineEmail, setOfflineEmail] = useState("");
    const [offlineMessage, setOfflineMessage] = useState("");
    const [sendingOffline, setSendingOffline] = useState(false);

    const reconnectRefreshTimer = useRef<number | null>(null);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState<
        Array<{
            id?: string;
            name: string;
            url: string;
            mime?: string | null;
            size?: number | null;
        }>
    >([]);


    function isImageAttachment(a: any) {
        const mime = String(a?.mime || "").toLowerCase();
        const url = String(a?.url || "").toLowerCase();

        return (
            mime.startsWith("image/") ||
            url.endsWith(".png") ||
            url.endsWith(".jpg") ||
            url.endsWith(".jpeg") ||
            url.endsWith(".gif") ||
            url.endsWith(".webp") ||
            url.endsWith(".svg")
        );
    }

    function resolveAttachmentUrl(url: string) {
        if (!url) return "";
        if (url.startsWith("http://") || url.startsWith("https://")) return url;
        return `${props.apiBase}${url}`;
    }

    async function uploadSelectedFile(file: File) {
        if (!file) return;

        try {
            setUploadingFile(true);

            const formData = new FormData();
            formData.append("file", file);

            const res = await fetch(
                `${props.apiBase}/public/conversations/${rt.conversationId}/upload`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${rt.token}`,
                    },
                    body: formData,
                }
            );

            const txt = await res.text().catch(() => "");
            const data = txt ? JSON.parse(txt) : null;

            if (!res.ok) {
                throw new Error(`upload failed (${res.status}) ${txt}`);
            }

            if (!data?.attachment) {
                throw new Error("UPLOAD_RESPONSE_INVALID");
            }

            setPendingAttachments((prev) => [...prev, data.attachment]);
            showNotice("success", t("notice.fileUploaded"));
        } catch (e: any) {
            console.error(e);
            showNotice("error", t("notice.fileFailed"));
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    }

    const refreshAfterReconnect = async () => {
        try {
            await loadHistory();

            await fetch(`${props.apiBase}/public/conversations/${rt.conversationId}/read`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${rt.token}`,
                },
            });
        } catch (e) {
            console.error("[cw] reconnect refresh failed", e);
        }
    };

    const rebootstrapInFlightRef = useRef(false);

    const rebootstrapAndReload = async () => {
        if (!props.onRebootstrap) return;
        if (rebootstrapInFlightRef.current) return;

        try {
            rebootstrapInFlightRef.current = true;
            setConnected(false);
            setAgentTyping(false);

            const b = await props.onRebootstrap();

            setRt({
                token: b.token,
                conversationId: b.conversationId,
            });

            setAgentStatus(toAgentStatus(b.agentStatus, b.anyAgentOnline));
            setClosed(false);
            if (b.contactKnown) setContactKnown(b.contactKnown);

            // history će se povući kroz postojeće effect-e kad se rt promeni
        } catch (e) {
            console.error("[cw] rebootstrap failed", e);
        } finally {
            rebootstrapInFlightRef.current = false;
        }
    };

    useEffect(() => {
        if (!pro?.enabled || props.conversationStarted) return;
        if (!props.preview) {
            try {
                if (sessionStorage.getItem(GREETING_SESSION_KEY)) return;
                if (Number(localStorage.getItem(GREETING_DISMISSED_KEY) || 0) > Date.now()) return;
            } catch {
                /* storage blocked - still fine to greet once */
            }
        }
        const timer = window.setTimeout(() => setGreetingDue(true), pro.delaySeconds * 1000);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!greetingDue || greetingDoneRef.current) return;
        if (open || hasConversationStarted) {
            // they found the chat on their own - no need to greet
            greetingDoneRef.current = true;
            return;
        }
        if (pro?.onlyWhenOnline && agentStatus !== "online") return; // wait until someone is online
        greetingDoneRef.current = true;
        setGreetingVisible(true);
        if (!props.preview) {
            try {
                sessionStorage.setItem(GREETING_SESSION_KEY, "1");
            } catch {
                /* ignore */
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [greetingDue, open, agentStatus, hasConversationStarted]);

    const openFromGreeting = () => {
        setGreetingVisible(false);
        setGreetingInChat(true);
        setOpen(true);
    };

    const dismissGreeting = () => {
        setGreetingVisible(false);
        if (props.preview) return;
        try {
            localStorage.setItem(GREETING_DISMISSED_KEY, String(Date.now() + GREETING_SNOOZE_MS));
        } catch {
            /* ignore */
        }
    };

    // opening the chat any other way puts the greeting inside it
    useEffect(() => {
        if (open && greetingVisible) {
            setGreetingVisible(false);
            setGreetingInChat(true);
        }
    }, [open, greetingVisible]);

    const submitPreChat = async () => {
        setPreChatTried(true);
        const name = contactName.trim();
        const emailVal = contactEmail.trim();
        if (preChatAskName && preChat?.name === "required" && !name) return;
        if (preChatAskEmail && preChat?.email === "required" && !emailVal) return;
        if (emailVal && !isValidEmail(emailVal)) return;
        if (!name && !emailVal) {
            markContactDone();
            return;
        }
        setSubmittingContact(true);
        try {
            const res = await fetch(`${props.apiBase}/public/visitors/contact`, {
                method: "POST",
                headers: { Authorization: `Bearer ${rt.token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ name: name || undefined, email: emailVal || undefined }),
            });
            if (!res.ok) throw new Error(`contact failed (${res.status})`);
            markContactDone();
            setContactKnown((k) => ({ name: k.name || !!name, email: k.email || !!emailVal }));
        } catch (e) {
            console.error(e);
            showNotice("error", t("notice.contactFailed"));
        } finally {
            setSubmittingContact(false);
        }
    };

    // after a closed chat: fresh conversation without reloading the page. Bootstrap reuses an
    // open conversation and creates a new one when the last one is closed.
    const [startingNewChat, setStartingNewChat] = useState(false);
    const startNewChat = async () => {
        if (startingNewChat) return;
        setStartingNewChat(true);
        try {
            await rebootstrapAndReload();
            setRated(false);
            setRatingValue(0);
            setRatingHover(0);
            setRatingComment("");
            setText("");
            setPendingAttachments([]);
            setShowTranscript(false);
        } finally {
            setStartingNewChat(false);
        }
    };

    const showNotice = (type: "success" | "error" | "info", text: string) => {
        setNotice({type, text});
        if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
        noticeTimer.current = window.setTimeout(() => setNotice(null), 2600);
    };

    const [rt, setRt] = useState(() => ({
        token: props.token,
        conversationId: props.conversationId,
    }));

    // offline form with ticketing: the message became ticket #n (answered by email), not a chat
    // message - remembered per conversation so a reload still shows it
    const ticketKey = `cw_ticket_sent:${rt.conversationId}`;
    const [ticketSent, setTicketSent] = useState<{ n: number; email: string } | null>(() => {
        try {
            return JSON.parse(sessionStorage.getItem(ticketKey) || "null");
        } catch {
            return null;
        }
    });


    // contact asked/answered once per conversation - remembered so a reload doesn't ask again
    useEffect(() => {
        try {
            setContactSubmitted(localStorage.getItem(`cw_contact_done:${rt.conversationId}`) === "1");
        } catch {
            /* ignore */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rt.conversationId]);
    const markContactDone = () => {
        setContactSubmitted(true);
        try {
            localStorage.setItem(`cw_contact_done:${rt.conversationId}`, "1");
        } catch {
            /* ignore */
        }
    };


    const socket: Socket | null = useMemo(() => {
        if (!props.realtimeUrl || !rt.token) return null;

        return io(`${props.realtimeUrl}/v`, {
            transports: ["websocket", "polling"],
            auth: {token: rt.token},
        });
    }, [props.realtimeUrl, rt.token]);

    // live visitors (console's Visitors page): tell the gateway which page this is, on
    // connect and whenever the address changes - polled, so single-page sites are covered
    // without touching the host page's history API
    useEffect(() => {
        if (!socket) return;
        let lastUrl = "";
        const sendPage = (force = false) => {
            // without the #fragment: an in-page anchor isn't a new page, and fragments can
            // carry sign-in tokens that operators have no business seeing
            const url = window.location.href.split("#")[0];
            if (!force && url === lastUrl) return;
            lastUrl = url;
            let timeZone = "";
            try {
                timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
            } catch {
                /* ignore */
            }
            socket.emit("v:page", { url, title: document.title, referrer: document.referrer, timeZone });
        };
        const onConnect = () => sendPage(true);
        socket.on("connect", onConnect);
        if (socket.connected) sendPage(true);
        const timer = window.setInterval(() => sendPage(), 2000);
        return () => {
            socket.off("connect", onConnect);
            window.clearInterval(timer);
        };
    }, [socket]);

    const emitTyping = (isTyping: boolean) => {
        if (!socket) return;
        socket.emit("v:typing", {isTyping});
    };


    function normalizeMsg(m: any): Msg {
        const senderType = (m.senderType ?? m.sender_type ?? "system") as Msg["senderType"];
        const deliveredAt = m.deliveredAt ?? m.delivered_at ?? null;
        const seenAt = m.seenAt ?? m.seen_at ?? null;

        return {
            messageId: String(m.messageId ?? m.id ?? crypto.randomUUID()),
            clientMsgId: m.clientMsgId ?? m.client_msg_id ?? null,
            senderType,
            body: String(m.body ?? ""),
            createdAt: String(m.createdAt ?? m.created_at ?? new Date().toISOString()),
            deliveredAt,
            seenAt,
            status: seenAt ? "seen" : deliveredAt ? "sent" : ((m.status ?? "sent") as Msg["status"]),
            senderName: m.sender_name ?? m.senderName ?? null,
            senderUserId: m.sender_user_id ?? m.senderUserId ?? null,
            metadata: m.metadata ?? null,
            attachments: Array.isArray(m.attachments) ? m.attachments : [],
        };
    }

    const resetAfterTranscript = async () => {
        // zatvori UI i očisti
        setOpen(false);
        setText("");
        setMessages([]);
        setAgentTyping(false);
        // setVisitorTyping(false); // (ti nemaš visitorTyping u widgetu; ignore ako ne postoji)
        setClosed(false);
        setEmail("");
        setShowTranscript(false);

        // rebootstrap (ako postoji)
        if (props.onRebootstrap) {
            const b = await props.onRebootstrap();
            setRt({token: b.token, conversationId: b.conversationId});
        }
    };

    useEffect(() => {
        setRt({token: props.token, conversationId: props.conversationId});
        setAgentStatus(toAgentStatus(props.agentStatus, props.anyAgentOnline));
    }, [props.token, props.conversationId, props.anyAgentOnline, props.agentStatus]);

    // autoscroll on new messages / open
    useEffect(() => {
        if (!open) return;
        endRef.current?.scrollIntoView({behavior: "smooth", block: "end"});
    }, [open, messages.length]);


    const openRef = useRef(false);

    useEffect(() => {
        openRef.current = open;
    }, [open]);


    useEffect(() => {
        if (!socket) return;

        const onConnect = () => {
            setConnected(true);
            everConnectedRef.current = true;
            if (reconnectNoticeTimer.current) window.clearTimeout(reconnectNoticeTimer.current);
            reconnectNoticeTimer.current = null;
            setReconnecting(false);
            setAgentTyping(false);

            if (reconnectRefreshTimer.current) {
                window.clearTimeout(reconnectRefreshTimer.current);
            }

            reconnectRefreshTimer.current = window.setTimeout(() => {
                refreshAfterReconnect().catch((e) => console.error("[cw] post-connect refresh failed", e));
            }, 150);
        };
        const onDisconnect = () => {
            setConnected(false);
            setAgentTyping(false);
            if (everConnectedRef.current && !reconnectNoticeTimer.current) {
                reconnectNoticeTimer.current = window.setTimeout(() => {
                    reconnectNoticeTimer.current = null;
                    setReconnecting(true);
                }, RECONNECT_NOTICE_DELAY_MS);
            }
        };

        const onMessageNew = (m: any) => {
            const nm = normalizeMsg(m);

            if (!openRef.current && nm.senderType === "agent" && nm.body) {
                setIncoming((prev) => ({
                    from: nm.senderName || t("msg.agent"),
                    body: nm.body,
                    count: (prev?.count ?? 0) + 1,
                }));
            }

            if (nm.senderType === "system" && nm.metadata?.escalateNotice === true) {
                setShowContactForm(true);
            }

            setMessages((prev) => {
                if (nm.clientMsgId) {
                    const idx = prev.findIndex((x) => x.clientMsgId === nm.clientMsgId);


                    if (idx >= 0) {
                        const copy = [...prev];
                        copy[idx] = {
                            ...copy[idx],
                            ...nm,
                            status: nm.seenAt ? "seen" : "sent",
                        };
                        return copy;
                    }
                }

                if (prev.some((x) => x.messageId === nm.messageId)) return prev;

                return [
                    ...prev,
                    {
                        ...nm,
                        status: nm.seenAt ? "seen" : "sent",
                    },
                ];
            });
        };

        const onTyping = (p: any) => {
            if (String(p?.conversationId ?? "") !== rt.conversationId) return;
            if (p?.actorType === "agent" || p?.actorType === "bot") {
                setTypingActor(p.actorType);
                setAgentTyping(!!p?.isTyping);
            }
        };

        const onConversationUpdated = (p: any) => {
            if (String(p?.conversationId ?? "") !== rt.conversationId) return;

            if (String(p?.status ?? "") === "closed") {
                setClosed(true);
                setRated(false);
                setAgentTyping(false);
                setConfirmClose(false);
                showNotice("info", t("notice.chatClosed"));
            }
        };

        const onConnectError = (err: any) => {
            const msg = String(err?.message || err || "");
            console.error("[cw] socket connect_error", msg);

            if (
                msg.includes("AUTH_FAILED") ||
                msg.includes("NO_TOKEN") ||
                msg.includes("INVALID") ||
                msg.includes("jwt") ||
                msg.includes("token")
            ) {
                rebootstrapAndReload().catch((e) => console.error("[cw] rebootstrap from connect_error failed", e));
            }
        };

        const onDelivered = (p: any) => {

            const messageId = String(p?.messageId ?? "");
            const clientMsgId = p?.clientMsgId ?? null;
            const deliveredAt = String(p?.deliveredAt ?? p?.delivered_at ?? new Date().toISOString());

            setMessages((prev) => {

                return prev.map((m) => {
                    if (clientMsgId && m.clientMsgId === clientMsgId) {
                        return { ...m, deliveredAt, status: m.seenAt ? "seen" : "sent" };
                    }
                    if (messageId && m.messageId === messageId) {
                        return { ...m, deliveredAt, status: m.seenAt ? "seen" : "sent" };
                    }
                    return m;
                });
            });
        };

        const onSeen = (p: any) => {
            const ids = Array.isArray(p?.messageIds) ? p.messageIds.map(String) : [];
            const seenAt = String(p?.seenAt ?? p?.seen_at ?? new Date().toISOString());

            if (!ids.length) return;

            setMessages((prev) =>
                prev.map((m) =>
                    ids.includes(m.messageId)
                        ? {...m, seenAt, status: "seen"}
                        : m
                )
            );
        };

        const onAgentsPresence = (p: any) => {
            setAgentStatus(toAgentStatus(p?.agentStatus, p?.anyAgentOnline));
        };

        socket.on("agents:presence", onAgentsPresence);
        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("message:new", onMessageNew);
        socket.on("typing", onTyping);
        socket.on("conversation:updated", onConversationUpdated);
        socket.on("connect_error", onConnectError);
        socket.on("message:delivered", onDelivered);
        socket.on("message:seen", onSeen);

        return () => {
            if (reconnectNoticeTimer.current) window.clearTimeout(reconnectNoticeTimer.current);
            reconnectNoticeTimer.current = null;
            socket.off("agents:presence", onAgentsPresence);
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            socket.off("message:new", onMessageNew);
            socket.off("typing", onTyping);
            socket.off("conversation:updated", onConversationUpdated);
            socket.off("connect_error", onConnectError);
            socket.off("message:delivered", onDelivered);
            socket.off("message:seen", onSeen);

            if (reconnectRefreshTimer.current) {
                window.clearTimeout(reconnectRefreshTimer.current);
                reconnectRefreshTimer.current = null;
            }

            socket.disconnect();
        };
    }, [socket, rt.conversationId]);

    useEffect(() => {
        return () => {
            if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
            if (reconnectRefreshTimer.current) window.clearTimeout(reconnectRefreshTimer.current);
            rebootstrapInFlightRef.current = false;
        };
    }, []);

    useEffect(() => {
        setMessages([]);
        setAgentTyping(false);
        setClosed(false);
    }, [rt.conversationId]);

    async function loadHistory() {
        const res = await fetch(`${props.apiBase}/public/conversations/${rt.conversationId}/messages?limit=50`, {
            headers: {Authorization: `Bearer ${rt.token}`},
        });
        if (!res.ok) throw new Error(`history failed (${res.status})`);
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        setMessages(items.map(normalizeMsg));
    }

    function isNearBottom(el: HTMLDivElement | null, threshold = 120) {
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    }

    // autoscroll on new messages / typing / open (only if near bottom)
    useEffect(() => {
        if (!open) return;

        const body = bodyRef.current;
        if (!isNearBottom(body)) return;

        // rAF da saceka layout (typing render)
        requestAnimationFrame(() => {
            endRef.current?.scrollIntoView({behavior: "smooth", block: "end"});
        });
    }, [open, messages.length, agentTyping]);

    useEffect(() => {
        if (!open) return;

        (async () => {
            try {
                await loadHistory();

                await fetch(`${props.apiBase}/public/conversations/${rt.conversationId}/read`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${rt.token}`,
                    },
                });
            } catch (e) {
                console.error("[cw] history/read failed", e);
            }
        })();
    }, [open, props.apiBase, rt.conversationId, rt.token]);

    useEffect(() => {
        return () => {
            if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
        };
    }, []);

    const send = () => {
        // Once a conversation has started, keep letting the visitor send —
        // messages queue for whichever agent claims/returns. Only a brand-new
        // conversation with no agent online is routed to the offline form
        // instead (see showOfflineForm).
        if (closed) return;

        const body = text.trim();
        if ((!body && pendingAttachments.length === 0) || !socket) return;

        const clientMsgId = crypto.randomUUID();

        setMessages((prev) => [
            ...prev,
            {
                messageId: clientMsgId,
                clientMsgId,
                senderType: "visitor",
                body,
                attachments: pendingAttachments,
                createdAt: new Date().toISOString(),
                status: "sending",
            },
        ]);

        socket.emit("v:message_create", {
            body,
            clientMsgId,
            attachments: pendingAttachments,
        });

        emitTyping(false);
        if (typingTimer.current) window.clearTimeout(typingTimer.current);
        typingTimer.current = null;

        setText("");
        setPendingAttachments([]);
    };

    function isValidEmail(v: string) {
        const s = (v || "").trim();
        if (!s) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    }

    const closeChat = async () => {
        try {
            const res = await fetch(`${props.apiBase}/public/conversations/${rt.conversationId}/close`, {
                method: "POST",
                headers: {Authorization: `Bearer ${rt.token}`},
            });

            if (!res.ok) {
                const t = await res.text().catch(() => "");
                throw new Error(`close failed (${res.status}) ${t}`);
            }

            setClosed(true);
            setRated(false);
            showNotice("info", t("notice.chatClosed"));
            setConfirmClose(false);
        } catch (e: any) {
            console.error(e);
            showNotice("error", t("notice.closeFailed"));
        }
    };

    const submitRating = async () => {
        if (!ratingValue) return;
        setSubmittingRating(true);
        try {
            const res = await fetch(`${props.apiBase}/public/conversations/${rt.conversationId}/rating`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${rt.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({rating: ratingValue, comment: ratingComment.trim() || undefined}),
            });

            if (!res.ok) {
                const t = await res.text().catch(() => "");
                throw new Error(`rating failed (${res.status}) ${t}`);
            }

            setRated(true);
            showNotice("success", t("notice.ratingThanks"));
        } catch (e: any) {
            console.error(e);
            showNotice("error", t("notice.ratingFailed"));
        } finally {
            setSubmittingRating(false);
        }
    };

    const submitContact = async () => {
        const name = contactName.trim();
        const emailVal = contactEmail.trim();
        if (!name && !emailVal) {
            setShowContactForm(false);
            markContactDone();
            return;
        }
        setSubmittingContact(true);
        try {
            const res = await fetch(`${props.apiBase}/public/visitors/contact`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${rt.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({name: name || undefined, email: emailVal || undefined}),
            });

            if (!res.ok) {
                const t = await res.text().catch(() => "");
                throw new Error(`contact failed (${res.status}) ${t}`);
            }

            markContactDone();
            setShowContactForm(false);
            showNotice("success", t("notice.contactThanks"));
        } catch (e: any) {
            console.error(e);
            showNotice("error", t("notice.contactFailed"));
        } finally {
            setSubmittingContact(false);
        }
    };

    const sendTranscript = async () => {
        const to = email.trim();
        if (!isValidEmail(to)) {
            showNotice("error", t("notice.invalidEmail"));
            return;
        }

        try {
            setSendingTranscript(true);

            const res = await fetch(`${props.apiBase}/public/conversations/${rt.conversationId}/transcript/email`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${rt.token}`,
                },
                body: JSON.stringify({email: to}),
            });

            const txt = await res.text().catch(() => "");
            const data = txt ? JSON.parse(txt) : null; // ✅ EVO "data"

            if (!res.ok) {
                throw new Error(`transcript failed (${res.status}) ${txt}`);
            }

            showNotice("success", t("notice.transcriptSent"));

            // ✅ ako backend kaže da je zatvorio convo -> reset + novi bootstrap
            if (data?.closed) {
                await resetAfterTranscript();
            } else {
                // ako ne zatvaraš na backendu, samo zatvori sheet
                setShowTranscript(false);
            }
        } catch (e: any) {
            console.error(e);
            showNotice("error", t("notice.transcriptFailed"));
        } finally {
            setSendingTranscript(false);
        }
    };

    async function sendOfflineMessage() {
        const name = offlineName.trim();
        const emailValue = offlineEmail.trim();
        const message = offlineMessage.trim();

        if (!message) {
            showNotice("error", t("notice.enterMessage"));
            return;
        }

        if (!isValidEmail(emailValue)) {
            showNotice("error", t("notice.invalidEmail"));
            return;
        }

        try {
            setSendingOffline(true);

            const res = await fetch(`${props.apiBase}/public/widget/offline-message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${rt.token}`,
                },
                body: JSON.stringify({
                    conversationId: rt.conversationId,
                    name,
                    email: emailValue,
                    message,
                }),
            });

            const txt = await res.text().catch(() => "");
            if (!res.ok) {
                throw new Error(`offline message failed (${res.status}) ${txt}`);
            }

            // no toast: the confirmation under the message says it (see pendingOfflineId)
            let parsed: { ticketNumber?: number } = {};
            try {
                parsed = JSON.parse(txt || "{}");
            } catch {
                /* ignore */
            }
            if (parsed.ticketNumber) {
                const sent = { n: parsed.ticketNumber, email: emailValue };
                setTicketSent(sent);
                try {
                    sessionStorage.setItem(ticketKey, JSON.stringify(sent));
                } catch {
                    /* ignore */
                }
            }
            markContactDone(); // they gave their email in the form
            setOfflineName("");
            setOfflineEmail("");
            setOfflineMessage("");
        } catch (e: any) {
            console.error(e);
            showNotice("error", t("notice.messageFailed"));
        } finally {
            setSendingOffline(false);
        }
    }

    function onLauncherPointerDown(e: ReactPointerEvent<HTMLButtonElement>) {
        if (e.button !== 0) return;
        const r = e.currentTarget.getBoundingClientRect();
        dragRef.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, left: r.left, top: r.top, moved: false };
        // capture right away: a fast first move can leave the 54px button before the
        // drag threshold is reached, and then the drag would be lost
        e.currentTarget.setPointerCapture(e.pointerId);
    }

    function onLauncherPointerMove(e: ReactPointerEvent<HTMLButtonElement>) {
        const d = dragRef.current;
        if (!d || d.pointerId !== e.pointerId) return;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        if (!d.moved) {
            if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
            d.moved = true;
            setDragging(true);
        }
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const left = clamp(d.left + dx, EDGE_GAP, vw - LAUNCHER_SIZE - EDGE_GAP);
        const top = clamp(d.top + dy, EDGE_GAP, vh - LAUNCHER_SIZE - EDGE_GAP);
        const side = left + LAUNCHER_SIZE / 2 < vw / 2 ? "left" : "right";
        setLauncherPos({
            side,
            x: side === "left" ? left : vw - left - LAUNCHER_SIZE,
            y: vh - top - LAUNCHER_SIZE,
        });
    }

    function onLauncherPointerUp(e: ReactPointerEvent<HTMLButtonElement>) {
        const d = dragRef.current;
        if (!d || d.pointerId !== e.pointerId) return;
        dragRef.current = null;
        if (d.moved) {
            suppressClickRef.current = true;
            setDragging(false);
            setLauncherPos((p) => {
                if (p) saveLauncherPos(p);
                return p;
            });
        }
    }

    // Double-click resets a dragged launcher to its default spot. Only a moved launcher
    // waits for a possible second click; otherwise the chat opens immediately.
    function onLauncherClick() {
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
        }
        if (!launcherPos) {
            setOpen(true);
            return;
        }
        if (clickTimerRef.current) {
            window.clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
            clearLauncherPos();
            setLauncherPos(null);
            return;
        }
        clickTimerRef.current = window.setTimeout(() => {
            clickTimerRef.current = null;
            setOpen(true);
        }, DOUBLE_CLICK_MS);
    }

    // Effective position: dragged position (or configured side), clamped to the current
    // viewport; when open, pulled in further so the whole chat window stays on screen.
    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
    const vh = typeof window !== "undefined" ? window.innerHeight : 768;
    const side = launcherPos?.side ?? (props.position === "left" ? "left" : "right");
    let offsetX = clamp(launcherPos?.x ?? props.offsetX ?? 20, EDGE_GAP, vw - LAUNCHER_SIZE - EDGE_GAP);
    let offsetY = clamp(launcherPos?.y ?? props.offsetY ?? 20, EDGE_GAP, vh - LAUNCHER_SIZE - EDGE_GAP);
    if (open) {
        const winW = vw <= 420 ? vw - 24 : 380;
        const winH = vw <= 420 ? vh - 110 : 560;
        offsetX = Math.min(offsetX, Math.max(EDGE_GAP, vw - winW - EDGE_GAP));
        offsetY = Math.min(offsetY, Math.max(EDGE_GAP, vh - winH - EDGE_GAP));
    }

    return (
        <div
            className="cw"
            style={{
                right: side === "right" ? offsetX : undefined,
                left: side === "left" ? offsetX : undefined,
                bottom: offsetY,
                ["--cw-primary" as any]: props.primaryColor ?? "#111827",
            }}
        >
            {!open && (
                <button
                    className={`cw-launcher is-${agentStatus} ${dragging ? "is-dragging" : ""}`}
                    onClick={onLauncherClick}
                    onPointerDown={onLauncherPointerDown}
                    onPointerMove={onLauncherPointerMove}
                    onPointerUp={onLauncherPointerUp}
                    onPointerCancel={onLauncherPointerUp}
                    aria-label={agentAway ? t("launcher.openAway") : agentOnline ? t("launcher.openOnline") : t("launcher.openOffline")}
                    title={
                        (agentAway ? t("launcher.titleAway") : agentOnline ? t("launcher.titleOnline") : t("launcher.titleOffline")) +
                        (launcherPos ? t("launcher.resetHint") : "")
                    }
                >
                    <LauncherIcon value={props.launcherEmoji} size={24} />
                    <span className="cw-launcherDot" aria-hidden="true" />
                    {(incoming || greetingVisible) && (
                        <span className="cw-launcherBadge" aria-hidden="true">{incoming ? incoming.count : 1}</span>
                    )}
                </button>
            )}

            {!open && incoming && (
                <div className={`cw-greeting cw-greeting--${side}`} role="status" aria-live="polite">
                    <button
                        type="button"
                        className="cw-greetingClose"
                        onClick={() => setIncoming(null)}
                        aria-label={t("greeting.close")}
                        title={t("greeting.close")}
                    >
                        <CloseIcon size={12} />
                    </button>
                    <button type="button" className="cw-greetingBody" onClick={() => setOpen(true)}>
                        <span className="cw-greetingFrom">{incoming.from}</span>
                        <span className="cw-greetingText cw-greetingText--clamp">{incoming.body}</span>
                    </button>
                </div>
            )}

            {!open && !incoming && greetingVisible && (
                <div className={`cw-greeting cw-greeting--${side}`} role="status">
                    <button
                        type="button"
                        className="cw-greetingClose"
                        onClick={dismissGreeting}
                        aria-label={t("greeting.close")}
                        title={t("greeting.close")}
                    >
                        <CloseIcon size={12} />
                    </button>
                    <button type="button" className="cw-greetingBody" onClick={openFromGreeting}>
                        <span className="cw-greetingFrom">{props.title || t("header.defaultTitle")}</span>
                        <span className="cw-greetingText">{greetingText || t("greeting.default")}</span>
                    </button>
                </div>
            )}

            {open && (
                <div className="cw-window" role="dialog" aria-label="Chat widget">
                    {showWhatsNew && (
                        <div className="cw-whatsNew" role="dialog" aria-label={t("whatsNew.aria")}>
                            <div className="cw-whatsNewCard">
                                <div className="cw-whatsNewBadge">{t("whatsNew.badge")}</div>
                                <div className="cw-whatsNewTitle">{t("whatsNew.title")}</div>
                                <div className="cw-whatsNewList">
                                    {WHATS_NEW.items.map((it) => (
                                        <div className="cw-whatsNewItem" key={it.title}>
                                            <div className="cw-whatsNewIcon" aria-hidden="true">{it.icon}</div>
                                            <div>
                                                <div className="cw-whatsNewItemTitle">{t(it.title)}</div>
                                                <div className="cw-whatsNewItemText">{t(it.text)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button className="cw-whatsNewBtn" onClick={dismissWhatsNew} autoFocus>
                                    {t("whatsNew.ok")}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="cw-header">
                        <div className="cw-headerIcon">
                            <LauncherIcon value={props.launcherEmoji} size={20} />
                        </div>

                        <div className="cw-title">
                            <div className="cw-titleText">{headerTitle}</div>

                            <div className="cw-statusRow">
                                {reconnecting ? (
                                    <div className="cw-status is-reconnecting">
                                        <span className="cw-statusDot" />
                                        <span>{t("header.reconnecting")}</span>
                                    </div>
                                ) : (
                                    <div className={`cw-status is-${agentStatus}`}>
                                        <span className="cw-statusDot" />
                                        <span>{agentAway ? t("header.away") : agentOnline ? t("header.online") : t("header.offline")}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="cw-actions">
                            <button
                                className="cw-action"
                                onClick={() => setShowTranscript((v) => !v)}
                                aria-label={t("header.sendTranscript")}
                                title={t("header.sendTranscript")}
                                disabled={!open || !hasConversationStarted}
                            >
                                <MailIcon />
                            </button>

                            <button
                                className="cw-action"
                                onClick={() => setConfirmClose(true)}
                                aria-label={t("header.endChat")}
                                title={t("header.endChat")}
                                // an unanswered offline message keeps the conversation open (server enforces it too)
                                disabled={!open || closed || !hasConversationStarted || !!pendingOfflineId}
                            >
                                <EndChatIcon />
                            </button>

                            <button
                                className="cw-close"
                                onClick={() => setOpen(false)}
                                aria-label={t("header.close")}
                                title={t("header.close")}
                            >
                                <CloseIcon />
                            </button>
                        </div>
                    </div>

                    {notice && (
                        <div className={`cw-notice cw-notice--${notice.type}`}>
                            {notice.text}
                        </div>
                    )}

                    {showTranscript && (
                        <div className="cw-sheet">
                            <div className="cw-sheetTitle">{t("transcript.title")}</div>

                            <input
                                className="cw-sheetInput"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t("transcript.placeholder")}
                                inputMode="email"
                                autoComplete="email"
                            />

                            <div className="cw-sheetActions">
                                <button
                                    className="cw-sheetBtn cw-sheetBtn--ghost"
                                    onClick={() => setShowTranscript(false)}
                                    disabled={sendingTranscript}
                                >
                                    {t("btn.cancel")}
                                </button>

                                <button
                                    className="cw-sheetBtn"
                                    onClick={sendTranscript}
                                    disabled={sendingTranscript}
                                >
                                    {sendingTranscript ? t("btn.sending") : t("btn.send")}
                                </button>
                            </div>

                            <div className="cw-sheetHint">
                                {t("transcript.hint")}
                            </div>
                        </div>
                    )}

                    {confirmClose && (
                        <div className="cw-sheet">
                            <div className="cw-sheetTitle">{t("close.title")}</div>

                            <div className="cw-sheetHint" style={{ marginTop: 6 }}>
                                {t("close.hint")}
                            </div>

                            <div className="cw-sheetActions" style={{ marginTop: 10 }}>
                                <button
                                    className="cw-sheetBtn cw-sheetBtn--ghost"
                                    onClick={() => setConfirmClose(false)}
                                    disabled={sendingTranscript}
                                >
                                    {t("btn.cancel")}
                                </button>

                                <button
                                    className="cw-sheetBtn"
                                    onClick={async () => {
                                        setConfirmClose(false);
                                        setShowTranscript(false);
                                        await closeChat();
                                    }}
                                    disabled={closed || sendingTranscript}
                                >
                                    {t("close.confirm")}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="cw-body" ref={bodyRef}>
                        {greetingInChat && !hasConversationStarted && (
                            <div className="cw-msg cw-msg--them">
                                <div className="cw-bubble">
                                    <div className="cw-senderName">{props.title || t("header.defaultTitle")}</div>
                                    <div className="cw-text">{greetingText || t("greeting.default")}</div>
                                </div>
                            </div>
                        )}
                        {messages.map((m) => {
                            const isSystem = (m.senderType ?? "") === "system";

                            if (isSystem) {
                                return (
                                    <div key={m.messageId} className="cw-system">
                                        <span className="cw-systemChip">{systemText(m, t)}</span>
                                    </div>
                                );
                            }

                            const mine = isMine(m);

                            return (
                                <div key={m.messageId} className={`cw-msg ${mine ? "cw-msg--me" : "cw-msg--them"}`}>
                                    <div className="cw-bubble">
                                        {!mine && (m.senderType === "agent" || m.senderType === "bot") && (
                                            <div className="cw-senderName">{m.senderName ?? t("msg.agent")}</div>
                                        )}

                                        <div className="cw-text">{m.body}</div>

                                        {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                                            <div className="cw-attachments">
                                                {m.attachments.map((a, i) => {
                                                    const href = resolveAttachmentUrl(a.url);

                                                    return (
                                                        <div key={`${href}-${i}`} className="cw-attachmentBlock">
                                                            {isImageAttachment(a) ? (
                                                                <a href={href} target="_blank" rel="noreferrer" className="cw-imageLink">
                                                                    <img
                                                                        src={href}
                                                                        alt={a.name || t("msg.imageAlt")}
                                                                        className="cw-imageAttachment"
                                                                    />
                                                                </a>
                                                            ) : null}

                                                            <a
                                                                href={href}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="cw-attachment"
                                                            >
                                                                {a.name || t("msg.attachment")}
                                                            </a>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        <div className="cw-meta">
                                            <span>{formatTime(m.createdAt, lang)}</span>

                                            {mine && (
                                                <>
                                                    <span className="cw-dot">•</span>
                                                    <span>
                                                    {m.status === "sending"
                                                        ? t("msg.status.sending")
                                                        : m.status === "seen" && !m.metadata?.offline
                                                            ? t("msg.status.seen")
                                                            : m.status === "failed"
                                                                ? t("msg.status.failed")
                                                                : t("msg.status.sent")}
                                                </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {m.messageId === pendingOfflineId ? (
                                        // confirmation instead of a bare "sent": it's a message for later, not a live chat
                                        <div className="cw-offlineReceivedRow">
                                            <div className="cw-offlineReceived">
                                                <span className="cw-offlineReceivedIcon" aria-hidden="true">✓</span>
                                                <span>
                                                    {offlineReplyEmail
                                                        ? t("offline.received", { email: offlineReplyEmail })
                                                        : t("offline.receivedNoEmail")}
                                                </span>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}

                        {agentTyping && (
                            <div className="cw-typing">
                                {typingActor === "bot" ? t("typing.bot") : t("typing.agent")}
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>

                    {(showContactForm || askContactOffline || askContactAway) && !contactSubmitted && !closed && !showPreChat && (
                        <div className="cw-contactForm">
                            <div className="cw-offlineTitle">
                                {askContactOffline ? t("contact.offlineTitle") : askContactAway ? t("contact.awayTitle") : t("contact.title")}
                            </div>
                            <div className="cw-offlineText">
                                {askContactOffline ? t("contact.offlineText") : askContactAway ? t("contact.awayText") : t("contact.text")}
                            </div>

                            <input
                                className="cw-input"
                                value={contactName}
                                onChange={(e) => setContactName(e.target.value)}
                                placeholder={t("contact.name")}
                            />

                            <input
                                className="cw-input"
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                                placeholder={askContactOffline ? t("offline.email") : t("contact.emailOptional")}
                                inputMode="email"
                                autoComplete="email"
                            />

                            <div className="cw-contactActions">
                                <button
                                    className="cw-send cw-send--offline"
                                    onClick={submitContact}
                                    disabled={submittingContact}
                                >
                                    {submittingContact ? t("btn.sending") : t("btn.send")}
                                </button>
                                <button
                                    type="button"
                                    className="cw-ratingSkip"
                                    onClick={() => {
                                        setShowContactForm(false);
                                        markContactDone();
                                    }}
                                >
                                    {t("btn.skip")}
                                </button>
                            </div>
                        </div>
                    )}

                    {showOfflineForm && ticketSent ? (
                        <div className="cw-offline">
                            <div className="cw-offlineTitle">{t("ticket.receivedTitle")}</div>
                            <div className="cw-offlineText">
                                {t("ticket.receivedText", { n: ticketSent.n, email: ticketSent.email })}
                            </div>
                            <button
                                className="cw-send cw-send--offline"
                                onClick={() => {
                                    setTicketSent(null);
                                    try {
                                        sessionStorage.removeItem(ticketKey);
                                    } catch {
                                        /* ignore */
                                    }
                                }}
                            >
                                {t("ticket.another")}
                            </button>
                        </div>
                    ) : showOfflineForm ? (
                        <div className="cw-offline">
                            <div className="cw-offlineTitle">{t("offline.title")}</div>
                            <div className="cw-offlineText">
                                {t("offline.text")}
                            </div>

                            <input
                                className="cw-input"
                                value={offlineName}
                                onChange={(e) => setOfflineName(e.target.value)}
                                placeholder={t("contact.name")}
                            />

                            <input
                                className="cw-input"
                                value={offlineEmail}
                                onChange={(e) => setOfflineEmail(e.target.value)}
                                placeholder={t("offline.email")}
                                inputMode="email"
                                autoComplete="email"
                            />

                            <textarea
                                className="cw-textarea"
                                value={offlineMessage}
                                onChange={(e) => setOfflineMessage(e.target.value)}
                                placeholder={t("offline.message")}
                                rows={4}
                            />

                            <button
                                className="cw-send cw-send--offline"
                                onClick={sendOfflineMessage}
                                disabled={
                                    sendingOffline ||
                                    !offlineMessage.trim() ||
                                    !isValidEmail(offlineEmail)
                                }
                            >
                                {sendingOffline ? t("btn.sending") : t("btn.sendMessage")}
                            </button>
                        </div>
                    ) : closed && !rated ? (
                        <div className="cw-rating">
                            <div className="cw-ratingTitle">{t("rating.title")}</div>
                            <div className="cw-ratingStars">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={`cw-star ${(ratingHover || ratingValue) >= n ? "is-filled" : ""}`}
                                        onMouseEnter={() => setRatingHover(n)}
                                        onMouseLeave={() => setRatingHover(0)}
                                        onClick={() => setRatingValue(n)}
                                        aria-label={t("rating.star", { n })}
                                    >
                                        ★
                                    </button>
                                ))}
                            </div>

                            {ratingValue > 0 && (
                                <textarea
                                    className="cw-textarea"
                                    value={ratingComment}
                                    onChange={(e) => setRatingComment(e.target.value)}
                                    placeholder={t("rating.comment")}
                                    rows={2}
                                />
                            )}

                            <div className="cw-ratingActions">
                                <button
                                    className="cw-send cw-send--offline"
                                    onClick={submitRating}
                                    disabled={!ratingValue || submittingRating}
                                >
                                    {submittingRating ? t("btn.sending") : t("btn.submit")}
                                </button>
                                <button type="button" className="cw-ratingSkip" onClick={() => setRated(true)}>
                                    {t("rating.later")}
                                </button>
                            </div>

                            {props.onRebootstrap && (
                                <button
                                    type="button"
                                    className="cw-newChat cw-newChat--secondary"
                                    onClick={startNewChat}
                                    disabled={startingNewChat}
                                >
                                    {startingNewChat ? t("btn.startingNewChat") : t("btn.newChat")}
                                </button>
                            )}
                        </div>
                    ) : showPreChat ? (
                        <div className="cw-contactForm cw-preChat">
                            <div className="cw-offlineTitle">{t("preChat.title")}</div>
                            <div className="cw-offlineText">{preChat?.intro || t("preChat.text")}</div>

                            {preChatAskName && (
                                <input
                                    className={`cw-input ${preChatTried && preChat?.name === "required" && !contactName.trim() ? "is-invalid" : ""}`}
                                    value={contactName}
                                    onChange={(e) => setContactName(e.target.value)}
                                    placeholder={preChat?.name === "required" ? t("preChat.name") : t("preChat.nameOptional")}
                                    autoComplete="name"
                                    maxLength={80}
                                />
                            )}
                            {preChatAskEmail && (
                                <input
                                    className={`cw-input ${
                                        preChatTried &&
                                        ((preChat?.email === "required" && !contactEmail.trim()) ||
                                            (!!contactEmail.trim() && !isValidEmail(contactEmail)))
                                            ? "is-invalid"
                                            : ""
                                    }`}
                                    value={contactEmail}
                                    onChange={(e) => setContactEmail(e.target.value)}
                                    onKeyDown={(e) => (e.key === "Enter" ? submitPreChat() : null)}
                                    placeholder={preChat?.email === "required" ? t("preChat.email") : t("preChat.emailOptional")}
                                    inputMode="email"
                                    autoComplete="email"
                                    maxLength={254}
                                />
                            )}
                            {preChatTried && !!contactEmail.trim() && !isValidEmail(contactEmail) && (
                                <div className="cw-fieldError">{t("notice.invalidEmail")}</div>
                            )}

                            <button
                                className="cw-send cw-send--offline"
                                onClick={submitPreChat}
                                disabled={submittingContact}
                            >
                                {submittingContact ? t("btn.sending") : t("preChat.start")}
                            </button>
                        </div>
                    ) : closed && props.onRebootstrap ? (
                        <div className="cw-ended">
                            <div className="cw-endedText">{t("closed.ended")}</div>
                            <button
                                type="button"
                                className="cw-send cw-send--offline cw-newChat"
                                onClick={startNewChat}
                                disabled={startingNewChat}
                            >
                                {startingNewChat ? t("btn.startingNewChat") : t("btn.newChat")}
                            </button>
                        </div>
                    ) : (
                        <>
                            {pendingAttachments.length > 0 && (
                                <div className="cw-pendingAttachments">
                                    {pendingAttachments.map((a, i) => (
                                        <div key={`${a.url}-${i}`} className="cw-pendingAttachment">
                                            <span>{a.name}</span>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setPendingAttachments((prev) =>
                                                        prev.filter((_, idx) => idx !== i)
                                                    )
                                                }
                                                aria-label={t("composer.removeAttachment")}
                                            >
                                                <CloseIcon size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="cw-footer">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            uploadSelectedFile(file);
                                        }
                                    }}
                                />

                                <button
                                    type="button"
                                    className="cw-attach"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={!connected || closed || uploadingFile}
                                    title={t("composer.attach")}
                                    aria-label={t("composer.attach")}
                                >
                                    {uploadingFile ? "…" : <PaperclipIcon />}
                                </button>

                                <input
                                    className="cw-input"
                                    value={text}
                                    disabled={!connected || closed}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setText(v);

                                        if (!socket || closed) return;

                                        emitTyping(true);

                                        if (typingTimer.current) {
                                            window.clearTimeout(typingTimer.current);
                                        }
                                        typingTimer.current = window.setTimeout(() => emitTyping(false), 900);
                                    }}
                                    onKeyDown={(e) => (e.key === "Enter" ? send() : null)}
                                    placeholder={
                                        !connected
                                            ? t("composer.connecting")
                                            : closed
                                                ? t("composer.closed")
                                                : agentAway
                                                    ? t("composer.away")
                                                : !agentOnline
                                                    ? t("composer.offline")
                                                    : pendingAttachments.length > 0
                                                        ? t("composer.withFile")
                                                        : t("composer.type")
                                    }
                                />

                                <button
                                    className="cw-send"
                                    onClick={send}
                                    disabled={
                                        !connected ||
                                        closed ||
                                        (!text.trim() && pendingAttachments.length === 0)
                                    }
                                >
                                    {t("btn.send")}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
